import moveArrayItem from 'array-move';
import { isNil } from 'lodash';
import { useBoardService } from 'src/features/boards';
import { v4 as uuid } from 'uuid';

import { findColumnIndexById } from '../../utils/findColumnIndexById';
import { CreateItemSlice, ItemSlice } from '../types';

const {
  addFile,
  addTask,
  deleteTask,
  fetchTask,
  getSubtasks,
  markTaskAsComplete,
  moveTask,
  removeFile,
  toggleUpovteTask,
  duplicateTask,
  updateTaskProperties,
} = useBoardService();

export const createItemSlice: CreateItemSlice = (set, get): ItemSlice => ({
  onCloseCurrentSelectedItem: (args) =>
    set((state) => {
      state.currentSelectedItem = {
        isOpen: false,
        columnId: undefined,
        itemIndex: undefined,
        mode: undefined,
        item: undefined,
        selectedDate: undefined,
        ...args,
      };
    }),
  onAddNewItem: async ({ columnKey, ...otherProps }) => {
    let foundColumnIndex = -1;
    set((state) => {
      foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnKey);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items.push({
          id: uuid(),
          comments: [],
          ...otherProps,
        });
        state.isLoading = true;
      }
    });

    if (foundColumnIndex > -1) {
      const payload = {
        boardId: get().boardId,
        colId: columnKey,
        type: otherProps.type,
        taskName: otherProps.name,
        assigner: otherProps.assigner,
        assignee: otherProps.assignee?.map((obj) => obj.memberId),
        duplicate: otherProps.duplicate?.map((obj) => obj.memberId),
        startDate: otherProps?.assignDate?.[0],
        endDate: otherProps?.assignDate?.[1],
        description: otherProps.description,
        priority: otherProps.priority,
        label: otherProps.label,
        creation: otherProps.creation?.creationId,
        attachments: otherProps.attachments,
        estimates: otherProps.estimates,
        top: otherProps.top,
        // * selectedTemplate contains id of the selected template while creating new task with the existing task template */
        selectedTemplate: otherProps.taskTemplateRef,
      };

      const task = await addTask(payload as any);
      if (task) {
        set((state) => {
          state.fetchBoard();
        });
        return task;
      }
    }
  },
  onDeleteItem: async ({ columnId, itemId }) => {
    let foundColumnIndex = -1;
    set((state) => {
      foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items = state.columns[foundColumnIndex].items
          .filter((item) => {
            return item.id !== itemId;
          })
          .map((item) => {
            if (item.subtasks?.length) {
              item.subtasks = item.subtasks.filter((subtask) => subtask.id !== itemId);
            }
            return item;
          });
      }
    });
    if (foundColumnIndex > -1) {
      await deleteTask({ id: itemId });
    }
  },
  onDuplicateItem: async ({ columnId, itemId }) => {
    let foundColumnIndex = -1;
    set(async (state) => {
      foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        // Commenting the optimization part of the code for the time being
        // // read the item index from the store
        // const itemIndex = state.columns[foundColumnIndex]?.items?.findIndex(
        //   (obj) => obj.id === itemId
        // );
        // if (itemIndex > -1) {
        //   const foundItem = state.columns[foundColumnIndex].items[itemIndex];
        //   const newId = uuid();
        //   const newTask = { id: newId, ...foundItem };
        //   console.log(newTask);
        //   state.columns[foundColumnIndex].items.push(newTask);
        // }
        //API call to duplicate task
        const newTask = await duplicateTask({ id: itemId });
        newTask && state.columns[foundColumnIndex].items.push(newTask);
        state.fetchBoard();
      }
    });
  },

  onUpdateItemName: ({ columnId, itemIndex, name }) =>
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items[itemIndex].name = name;
      }
    }),

  onUpdateItemField: async ({ item, keys, columnId, itemIndex }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
          (obj) => obj.id === item.id
        );
        if (foundTaskIndex > -1) {
          keys.map(
            (key) => (state.columns[foundColumnIndex].items[foundTaskIndex][key] = item[key])
          );
        } else {
          const foundTaskIndexWithSubTask = state.columns[foundColumnIndex].items.findIndex((i) =>
            (i.subtasks || []).find((st: any) => st._id === item.id)
          );
          if (foundTaskIndexWithSubTask > -1) {
            const foundSubtaskIndex =
              state.columns[foundColumnIndex].items[foundTaskIndexWithSubTask].subtasks?.findIndex(
                (st: any) => st._id === item.id
              ) || -1;
            if (foundSubtaskIndex > -1) {
              keys.map(
                (key) =>
                  ((
                    state.columns[foundColumnIndex].items[foundTaskIndexWithSubTask] as any
                  ).subtasks[foundSubtaskIndex][key] = item[key])
              );
            }
          }
        }
      }
    });
    const payload: any = {};
    keys.map((field) => {
      if (item[field] === undefined) {
        payload[field] = null;
      } else if (field === 'assignee') {
        payload[field] = item[field]?.map((obj) => obj.memberId);
      } else if (field === 'duplicate') {
        payload[field] = item[field]?.map((obj) => obj.memberId);
      } else if (field === 'assigner') {
        payload[field] = item.assigner;
      } else if (field === 'assignDate') {
        if (item?.assignDate?.length && item?.assignDate?.length > 1) {
          payload.start_date = item?.assignDate?.[0];
          payload.end_date = item?.assignDate?.[1];
        } else {
          payload.end_date = item?.assignDate?.[0];
          payload.start_date = null;
        }
      } else {
        payload[field] = item[field];
      }
    });
    payload.id = item.id;
    const itemR = await updateTaskProperties({ properties: payload, taskId: item.id });
    itemR && get().setItem({ columnId, itemId: itemR.id!, item: itemR });
    if (payload.duplicate) {
      get().fetchBoard();
    }
  },

  onUpdateItemPlacementState: ({ sourceColId, destColId, itemId, sourceIndex, destIndex }) => {
    set((state) => {
      const sourceColIndex = findColumnIndexById(state.columns, sourceColId);
      if (sourceColIndex < 0) return;
      const destColIndex = findColumnIndexById(state.columns, destColId);
      if (destColIndex < 0) return;
      if (isNil(sourceIndex))
        sourceIndex = state.columns[sourceColIndex].items.findIndex((obj) => obj.id === itemId);
      if (sourceIndex < 0) return;
      if (sourceColId !== destColId) {
        const item = state.columns[sourceColIndex].items[sourceIndex];
        if (!item) return;
        state.columns[destColIndex].items.splice(
          destIndex,
          0,
          state.columns[sourceColIndex].items[sourceIndex]
        );
        state.columns[sourceColIndex].items = state.columns[sourceColIndex].items.filter(
          (obj) => obj.id !== itemId
        );
      } else if (sourceColId === destColId) {
        state.columns[sourceColIndex].items = moveArrayItem(
          state.columns[sourceColIndex].items,
          sourceIndex,
          destIndex
        );
      }
    });
  },
  onUpdateItemPlacement: async ({
    sourceColId,
    destColId,
    itemId,
    sourceIndex,
    destIndex,
    fetchTimer,
    boardId,
  }) => {
    const newItemIndex = destIndex;
    get().onUpdateItemPlacementState({
      sourceColId,
      destColId,
      itemId,
      sourceIndex,
      destIndex,
    });
    const isMoved = !!(await moveTask({
      boardId: boardId || get().boardId,
      destColId: destColId,
      newItemIndex: newItemIndex,
      sourceColId,
      taskId: itemId,
    }));
    fetchTimer && (await fetchTimer());
    return isMoved;
    // if (isMoved) {
    //   get().fetchBoard();
    // }
  },
  currentSelectedItem: {},
  onChangeCurrentSelectedItem: async (arg) => {
    set((state) => {
      state.currentSelectedItem = arg;
    });
    if (arg.mode === 'edit' && arg.item?.id) {
      const item = await fetchTask({ id: arg.item.id });
      if (!item) return;
      item &&
        get().setItem({
          columnId: arg.columnId!,
          itemId: item.id!,
          item: item,
        });
    }
  },
  onAddFile: async ({ itemId, fileName, filePath, columnId, itemIndex }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items[itemIndex].attachments?.push();
      }
    });
    const item = await addFile(itemId, fileName, filePath);

    if (!item) return;
    item &&
      get().setItem({
        columnId: columnId,
        itemId: itemId,
        item: item,
      });
  },
  onRemoveFile: async ({ itemId, filePath, columnId, itemIndex }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items[itemIndex].attachments?.push();
      }
    });
    const item = await removeFile(itemId, filePath);

    if (!item) return;
    item &&
      get().setItem({
        columnId: columnId,
        itemId: itemId,
        item: item,
      });
  },
  setItem: async ({ columnId, itemId, item }) => {
    set((s) => {
      const colIndex = s.columns.findIndex((obj) => obj.id === columnId);
      const itemIndex = s.columns[colIndex]?.items?.findIndex((obj) => obj.id === itemId);
      if (colIndex > -1 && itemIndex > -1) {
        s.columns[colIndex].items[itemIndex] = { ...s.columns[colIndex].items[itemIndex], ...item };
      } else if (colIndex > -1 && itemIndex < 0) {
        const itemWithSubItemIndex = s.columns[colIndex]?.items?.findIndex((i) =>
          (i.subtasks || []).find((st: any) => st._id === itemId)
        );
        if (itemWithSubItemIndex > -1) {
          const subItemIndex = (
            s.columns[colIndex]?.items[itemWithSubItemIndex].subtasks || []
          ).findIndex((st: any) => st._id === itemId);
          (s.columns[colIndex].items[itemWithSubItemIndex] as any).subtasks[subItemIndex] = {
            ...(s.columns[colIndex].items[itemWithSubItemIndex] as any).subtasks[subItemIndex],
            ...item,
          };
        }
      }
    });
  },

  onToggleTaskUpvote: async ({ columnId, itemIndex, upvote = true, itemId }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        state.columns[foundColumnIndex].items[itemIndex].vote = upvote ? 1 : -1;
        state.columns[foundColumnIndex].items[itemIndex].votes =
          state.columns[foundColumnIndex].items[itemIndex]?.votes || 0 + -(upvote ? 1 : -1);
      }
    });
    const task = await toggleUpovteTask(itemId, upvote);
    // task && get().setItem({ columnId, itemId, item: task });
  },
  onMarkAsComplete: async ({ columnId, completed = true, itemId }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
          (obj) => obj.id === itemId
        );
        state.columns[foundColumnIndex].items[foundTaskIndex].completed = completed;
      }
    });
    const task = await markTaskAsComplete(itemId, completed);
    task && get().setItem({ columnId, itemId, item: task });
  },
  onMarkAsCompleteSubtask: async ({ columnId, completed = true, taskId, subtaskId }) => {
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
          (obj) => obj.id === taskId
        );
        const foundSubtaskIndex = state.columns[foundColumnIndex].items[
          foundTaskIndex
        ]?.subtasks?.findIndex((obj) => obj.id === subtaskId);
        const item = state.columns[foundColumnIndex]?.items?.[foundTaskIndex];
        if (item.completedSubTaskTotal !== undefined) {
          if (completed) {
            item.completedSubTaskTotal += 1;
          } else {
            item.completedSubTaskTotal -= 1;
          }
        }
        if (item.subtasks && foundSubtaskIndex !== undefined) {
          item.subtasks[foundSubtaskIndex].completed = completed;
        }
      }
    });

    await markTaskAsComplete(subtaskId, completed);
  },
  onFetchSubtasks: async ({ columnId, taskId }) => {
    const tasks = await getSubtasks(taskId);
    if (!tasks) return;
    set((state) => {
      const foundColumnIndex = state.columns.findIndex((obj) => obj.id === columnId);
      if (foundColumnIndex > -1) {
        const foundTaskIndex = state.columns[foundColumnIndex].items.findIndex(
          (obj) => obj.id === taskId
        );
        state.columns[foundColumnIndex].items[foundTaskIndex].subtasks = [...tasks];
      }
    });
  },
});
